/*
 * File: App.js
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on June 16, 2022 at 1:03 PM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: May 18, 2023 at 12:39 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useRef
} from 'react';
import {
	Routes, Route, useLocation, useSearchParams
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import PropTypes from 'prop-types';

// Utilities
import { toastError, toastMessage, toastSuccess } from '../utilities/toaster';
import { filterData } from '../utilities/state';
import { scrollPageTo } from '../utilities/position';

// Styles
import { GlobalStyles } from '../styles/global';
import { DarkTheme, LightTheme } from '../styles/colors';

// Slices
import { updateLocale, clearLocale } from '../store/slices/locale/locale.slice';
import { updateMerchant, clearMerchant } from '../store/slices/merchant/merchant.slice';
import { updateConsent, initializeConsent } from '../store/slices/consent/consent.slice';
import { pushComponentState } from '../store/slices/component/component.slice';
import { fetchCart } from '../store/slices/cart/cart.slice';

// Components
import {
	Spinner,
	CookieBar
} from '../components';

// Routes
import { Routes as RoutesCreator } from './Routes';

// Constants
import { UI_MODE_OPTIONS, ALERT_STYLES } from '../../Constants';

// Configuration
import { CORE } from '../../merchant.config';


/**
 * Constants
 */

const isProduction = process.env.ENV === 'production';
const isEU = false; // For development / hot usage only (does not affect production)


/**
 * Component
 */

const App = (data) => {

	// Set component props from server
	let props = data || {};
	const { isHot } = props;

	// Get default merchant if hot mode
	if (isHot === true) {
		props = {
			...props,
			merchant: CORE
		};
	}

	// Use hooks
	const location = useLocation();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams({});

	// Initialize render count
	const renderCount = useRef(-1);

	// Get current user from hook
	const cartStatus = useSelector((state) => state.cart.status);

	// Get current consent state from hook
	const consentStatus = useSelector((state) => state.consent.status);
	const currentConsent = useSelector((state) => state.consent.currentValue);
	const previousConsent = useSelector((state) => state.consent.previousValue);

	// Get current UI mode from hook
	let uiMode = useSelector((state) => state.ui.value);
	if (props?.state?.ui) uiMode = props?.state?.ui;

	// Get component state on client
	if (typeof window !== 'undefined') {
		try { props = JSON.parse(decodeURIComponent(window.document.getElementById('data-state').innerHTML)); } catch (e) { }
	}

	// Get current merchant from hook
	const clientMerchant = useSelector((state) => state.merchant.value);
	const stateMerchant = props?.merchant || clientMerchant;

	// Define current theme
	const currentTheme = uiMode.mode === UI_MODE_OPTIONS.DARK ? DarkTheme(stateMerchant) : LightTheme(stateMerchant);

	// Handle actions on component load
	useEffect(() => {

		// Fetch current cart
		dispatch(fetchCart(stateMerchant));

		// Set current merchant in state
		if (props?.merchant && props?.merchant?.id) dispatch(updateMerchant(props?.merchant));
		else dispatch(clearMerchant());

		// Set locale in state
		if (props.locale && props.locale.localeId) dispatch(updateLocale(props.locale));
		else dispatch(clearLocale());

		// Set consent in state
		if (props?.state?.consent) dispatch(updateConsent(props.state.consent));
		else if (isHot === true) dispatch(initializeConsent(isEU));

		// Set component data in state
		if (props.data && Object.keys(props.data).length > 0) {

			// Filter props for route
			const filteredData = filterData(window.location.pathname, props.data);

			// Push primary component state
			dispatch(pushComponentState(filteredData));

		} else {

			// Push primary component state
			dispatch(pushComponentState());
		}

		// Check for alert to display
		if (props?.alert) {

			// Get alert parameters
			const { message, style } = props.alert;

			// Show toast
			setTimeout(() => {
				if (style === ALERT_STYLES.INFO) {
					toastMessage(uiMode, stateMerchant, message);
				} else if (style === ALERT_STYLES.ERROR) {
					toastError(uiMode, stateMerchant, message);
				}
			}, 400);
		}
	}, []);

	// Handle actions on consent update action
	useEffect(() => {

		// Only update if consent has been set
		if (consentStatus !== 'initial') {

			// Only update if production environment
			if (isProduction) {

				// Check if consent values changed
				const performanceChanged = currentConsent.performanceEnabled !== previousConsent.performanceEnabled;
				const analyticsChanged = currentConsent.analyticsEnabled !== previousConsent.analyticsEnabled;
				const targetingChanged = currentConsent.targetingEnabled !== previousConsent.targetingEnabled;

				// Check if performance consent changed
				if (performanceChanged) {
					if (currentConsent.performanceEnabled === true) {

						// Push GTM trigger
						try {
							window.dataLayer.push({ event: 'consent_given_performance' });
						} catch (err) { }

					} else {

						// Reload page to disable scripts
						window.location.reload(false);

						// Return
						return;
					}
				}

				// Check if analytics consent changed
				if (analyticsChanged) {
					if (currentConsent.analyticsEnabled === true) {

						// Push GTM trigger
						try {
							window.dataLayer.push({ event: 'consent_given_analytics' });
						} catch (err) { }

					} else {

						// Reload page to disable scripts
						window.location.reload(false);

						// Return
						return;
					}
				}

				// Check if targeting consent changed
				if (targetingChanged) {
					if (currentConsent.targetingEnabled === true) {

						// Push GTM trigger
						try {
							window.dataLayer.push({ event: 'consent_given_targeting' });
						} catch (err) { }

					} else {

						// Reload page to disable scripts
						window.location.reload(false);
					}
				}
			}
		}
	}, [currentConsent]);

	// Update render count
	useEffect(() => {
		renderCount.current += 1;
	});

	// Handle actions on route change
	useEffect(() => {

		// Get query parameters
		const queryObj = Object.fromEntries([...searchParams]);

		// Check for error query
		if (searchParams.has('error')) {

			// Get error message
			const errorMessage = decodeURIComponent(searchParams.get('error'));

			// Clear error query parameters
			const { error, code, ...additionalParams } = queryObj;
			setSearchParams(additionalParams);

			// Display error toast
			setTimeout(() => {
				toastError(uiMode, stateMerchant, errorMessage);
			}, 400);
		}

		// Check for success query
		if (searchParams.has('success')) {

			// Get success message
			const successMessage = decodeURIComponent(searchParams.get('success'));

			// Clear success query parameters
			const { success, ...additionalParams } = queryObj;
			setSearchParams(additionalParams);

			// Display success toast
			setTimeout(() => {
				toastSuccess(uiMode, stateMerchant, successMessage);
			}, 400);
		}

		// Handle hash scroll
		const { hash } = location;
		if (!hash) {
			scrollPageTo(0, 0, { behavior: 'smooth' });
		} else {
			const id = hash.replace('#', '');
			const element = document.getElementById(id);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			} else {
				scrollPageTo(0, 0, { behavior: 'smooth' });
			}
		}
	}, [location]);

	// Create routes with props
	const RoutesManifest = RoutesCreator(props, isHot);

	// Set up router render function
	const renderRouter = () => (
		<Routes>
			{RoutesManifest.map((route, index) => {
				const { component: Component } = route;
				return <Route key={route.path || `route-${index}`} path={route.path} element={Component} />;
			})}
		</Routes>
	);

	// Set up component render function
	const renderComponent = () => {
		if (cartStatus === 'loading') {
			return <Spinner merchant={stateMerchant} showMeta />;
		}
		if (isProduction && currentConsent.performanceEnabled === true) {
			return (
				<GoogleReCaptchaProvider reCaptchaKey={process.env.RECAPTCHA_PUBLIC_KEY}>
					{renderRouter()}
				</GoogleReCaptchaProvider>
			);
		}
		return renderRouter();
	};

	// Render router
	return (
		<ThemeProvider theme={currentTheme}>
			<GlobalStyles />
			{renderComponent()}
			{currentConsent.consentActionTaken !== true && consentStatus !== 'initial' && <CookieBar merchant={stateMerchant} />}
		</ThemeProvider>
	);
};


/**
 * Configuration
 */

App.propTypes = {
	locale: PropTypes.shape(),
	state: PropTypes.shape(),
	alert: PropTypes.shape(),
	data: PropTypes.shape(),
	merchant: PropTypes.shape(),
	isHot: PropTypes.bool
};
App.defaultProps = {
	locale: {},
	state: {},
	alert: {},
	merchant: {},
	data: null,
	isHot: false
};


/**
 * Exports
 */

export default App;
